/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Button,
  CardContent,
  CardHeader,
  Grid,
  Typography
} from '@mui/material';
import { MFAProps } from '@propTypes/MFAProps';
import copy from "copy-to-clipboard";
import { InlineAlert } from "common/alerts";
import { LoginStyles } from "customStyles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLoginStore } from "state/LoginStore";
import { getAppLogo } from '../../utils/getAppLogo';
import { useAuth } from "../../hooks/useAuth";
import { useMUITheme } from "../../theme/ThemeProvider";
import { errorTypes } from "../../utils/errorTypes";
import { fullPolicyLanguageComponenet } from "../../utils/legalLanguage";
import {
  InitialLegalLanguage,
  ListOfPWRequirements,
  MFAPwButton,
  MFAPwField,
  MFATextInput,
  passwordRequirements,
  SetUpMFAComponent
} from "./components";
import { defineAlertStyle, defineFooterButtonsStyle } from "./customStyles";
import { defineIsButtonDisabled, DefineIsButtonDisabledProps } from "./helpers";

type Flow =
  | "DEFAULT"
  | "LEGAL_NOTICE"
  | "MFA_VERIFY"
  | "MFA_SETUP"
  | "RESET_PASSWORD"
  | "FORCE_RESET_PASSWORD"
  | "EXPIRED_RESET_PASSWORD"
  | "FORGOT_PASSWORD_USERNAME";

type InputMode = "verification" | "username" | null;

export const MFAPasswordReset: React.FC<MFAProps> = ({
  userName,
  resetPassword,
  cancel,
  cancelWithMessage,
  stateLogo,
}) => {
  const {
    handleMFASubmit,
    mfa,
    authCode,
    userForgotPassword,
    handleResetPassword,
    handleAcceptTerms,
    authFlowError,
    sendForgotPassCode,
    forceResetPassword,
    cancelFlow,
    authFlowErrorType,
    loading,
    setAuthFlowError
  } = useAuth();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmedPass, setShowConfirmedPass] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [userNameValue, setUserNameValue] = useState("");
  const [currentPass, setCurrentPass] = useState("");
  const [oldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [finalPassword, setPassword] = useState("");
  const [pwValidationMsg, setPwValidationMsg] = useState("");
  const [verificationMsg, setVerificationMsg] = useState("");
  const [viewingPrivacyPolicy, setViewingPrivacyPolicy] = useState(false);
  const [expiredResetPass] = useState(false);

  const [hasSubmittedMfa, setHasSubmittedMfa] = useState(false);

  const inputRef = useRef<any>(null);
  const btnRef = useRef<any>(null);

  const { mode } = useMUITheme();
  const isDark = mode === 'dark';

  const userAcceptingTerms = useLoginStore((state) => state.userAcceptingTerms);
  const errorMessage = useLoginStore((state) => state.errorMessage);
  const errorDescription = useLoginStore((state) => state.errorDescription);
  const copySuccess = useLoginStore((state) => state.copySuccess);
  const setCopySuccess = useLoginStore((state) => state.setCopySuccess);
  const setErrorMessage = useLoginStore((state) => state.setErrorMessage);
  const setErrorDescription = useLoginStore((state) => state.setErrorDescription);
  const severity = useLoginStore((state) => state.severity);
  const setSeverity = useLoginStore((state) => state.setSeverity);
  const openAlert = useLoginStore((state) => state.openAlert);
  const setOpenAlert = useLoginStore((state) => state.setOpenAlert);
  const stateName = useLoginStore((state) => state.stateName);


  const onlyNumbersForVerification = /^\d*$/.test(resetCode);
  const hasAuthCode = !!authCode?.text;
  const isMfaActive = !!mfa && !!handleMFASubmit;
  const shouldShowLegalNotice = userAcceptingTerms && (!isMfaActive || hasSubmittedMfa);
  const flow: Flow = useMemo(() => {
    if (shouldShowLegalNotice) return "LEGAL_NOTICE";
    if (isMfaActive && !hasAuthCode) return "MFA_VERIFY";
    if (isMfaActive && hasAuthCode) return "MFA_SETUP";
    if (userForgotPassword && handleResetPassword) return "RESET_PASSWORD";
    if (forceResetPassword && handleResetPassword) return "FORCE_RESET_PASSWORD";
    if (expiredResetPass) return "EXPIRED_RESET_PASSWORD";
    if (userName && !userForgotPassword) return "FORGOT_PASSWORD_USERNAME";
    return "DEFAULT";
  }, [
    shouldShowLegalNotice,
    isMfaActive,
    hasAuthCode,
    userForgotPassword,
    handleResetPassword,
    forceResetPassword,
    expiredResetPass,
    userName,
  ]);

  const isMFAVerify = flow === "MFA_VERIFY";
  const isMFASetup = flow === "MFA_SETUP";
  const isLegalNotice = flow === "LEGAL_NOTICE";
  const isResetPassword = flow === "RESET_PASSWORD";
  const isForceResetPassword = flow === "FORCE_RESET_PASSWORD";
  const isExpiredResetPassword = flow === "EXPIRED_RESET_PASSWORD";
  const isForgotPasswordUsername = flow === "FORGOT_PASSWORD_USERNAME";

  const viewConfig = useMemo(() => {
    switch (flow) {
      case "MFA_VERIFY":
        return {
          title: "Two-Factor Authenticator",
          subheader: "Please enter the code displayed on your authenticator application.",
          buttonLabel: "VERIFY",
          inputId: "verification-code",
          inputLabel: "Verification Code",
          inputName: "Verification Code",
          inputMode: "verification" as InputMode,
          showTextInput: true,
          showPasswordFields: false,
          showCancelButton: true,
          showBackButton: false,
          showFooter: true,
          showLogoFooter: false,
        };

      case "MFA_SETUP":
        return {
          title: "Enable Two-Factor Authentication",
          subheader:
            "Your organization requires you to set up two-factor authentication. Use a time-based authenticator to scan the QR code below or manually enter the key.",
          buttonLabel: "CONTINUE",
          inputId: "verification-code",
          inputLabel: "Verification Code",
          inputName: "Verification Code",
          inputMode: "verification" as InputMode,
          showTextInput: true,
          showPasswordFields: false,
          showCancelButton: true,
          showBackButton: false,
          showFooter: true,
          showLogoFooter: false,
        };

      case "LEGAL_NOTICE":
        return {
          title: "Notice",
          subheader: viewingPrivacyPolicy ? "" : (
            <InitialLegalLanguage setviewingPrivacyPolicy={setViewingPrivacyPolicy} />
          ),
          buttonLabel: "AGREE",
          inputId: "",
          inputLabel: "",
          inputName: "",
          inputMode: null as InputMode,
          showTextInput: false,
          showPasswordFields: false,
          showCancelButton: true,
          showBackButton: false,
          showFooter: true,
          showLogoFooter: false,
        };

      case "RESET_PASSWORD":
        return {
          title: "Change Password",
          subheader: "Enter the code that was sent to your email. Your new password must:",
          buttonLabel: "CHANGE PASSWORD",
          inputId: "reset-pw-code",
          inputLabel: "Reset Password Code",
          inputName: "reset-pw-code",
          inputMode: "verification" as InputMode,
          showTextInput: true,
          showPasswordFields: true,
          showCancelButton: false,
          showBackButton: true,
          showFooter: true,
          showLogoFooter: true,
        };

      case "FORCE_RESET_PASSWORD":
      case "EXPIRED_RESET_PASSWORD":
        return {
          title: "Create New Password",
          subheader: "A strong password helps prevent unauthorized access to your account. Your password must:",
          buttonLabel: "CREATE PASSWORD",
          inputId: "",
          inputLabel: "",
          inputName: "",
          inputMode: null as InputMode,
          showTextInput: false,
          showPasswordFields: true,
          showCancelButton: false,
          showBackButton: true,
          showFooter: true,
          showLogoFooter: true,
        };

      case "FORGOT_PASSWORD_USERNAME":
        return {
          title: "Forgot Password?",
          subheader: "Enter the username associated with your account and we'll send you a code to verify your account.",
          buttonLabel: "SEND RESET CODE",
          inputId: "userName",
          inputLabel: "Username",
          inputName: "userName",
          inputMode: "username" as InputMode,
          showTextInput: true,
          showPasswordFields: false,
          showCancelButton: false,
          showBackButton: true,
          showFooter: true,
          showLogoFooter: true,
        };

      default:
        return {
          title: "",
          subheader: "",
          buttonLabel: "",
          inputId: "",
          inputLabel: "",
          inputName: "",
          inputMode: null as InputMode,
          showTextInput: false,
          showPasswordFields: false,
          showCancelButton: false,
          showBackButton: false,
          showFooter: false,
          showLogoFooter: false,
        };
    }
  }, [flow, viewingPrivacyPolicy]);

  useEffect(() => {
    if (isMFAVerify) {
      inputRef.current?.focus?.({ cursor: "start" });
    } else if (isLegalNotice) {
      btnRef.current?.focus?.();
    }
  }, [isMFAVerify, isLegalNotice]);

  useEffect(() => {
    if (!mfa && !userAcceptingTerms) {
      setHasSubmittedMfa(false);
    }
  }, [mfa, userAcceptingTerms]);

  useEffect(() => {
    const message = authFlowError ?? "";
    const type = authFlowErrorType ?? "";

    if (errorTypes[message]) {
      setErrorMessage(errorTypes[message].title);
      setErrorDescription(errorTypes[message].description);
    } else if (errorTypes[type]) {
      setErrorMessage(errorTypes[type].title);
      setErrorDescription(errorTypes[type].description);
    } else {
      setErrorMessage(message);
      setErrorDescription("");
    }
  }, [authFlowErrorType, authFlowError, setErrorMessage, setErrorDescription]);

  useEffect(() => {
    const lengthValid = currentPass.length >= 16;
    const hasNumber = /\d/.test(currentPass);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(currentPass);
    const hasUpperCase = /[A-Z]/.test(currentPass);
    const hasLowerCase = /[a-z]/.test(currentPass);
    const onlyNumbersForVerification = /^\d*$/.test(resetCode);

    if (isForceResetPassword || isResetPassword) {
      if (!lengthValid) setErrorMessage(`Your Password Must ${passwordRequirements[0]}`);
      if (!hasNumber) setErrorMessage(`Your Password Must ${passwordRequirements[1]}`);
      if (!hasSpecialCharacter) setErrorMessage(`Your Password Must ${passwordRequirements[2]}`);
      if (!hasLowerCase) setErrorMessage(`Your Password Must ${passwordRequirements[4]}`);
      if (!hasUpperCase) setErrorMessage(`Your Password Must ${passwordRequirements[3]}`);

      if (!resetCode) {
        setVerificationMsg("")
      } else if (!onlyNumbersForVerification) {
        setVerificationMsg("Verification code must be numbers");
      } else {
        setVerificationMsg("Invalid verification code provided, please try again.");
      }

      if (
        lengthValid &&
        hasNumber &&
        hasSpecialCharacter &&
        hasLowerCase &&
        hasUpperCase &&
        currentPass !== newPass
      ) {
        setPwValidationMsg("Your passwords do not match");
      } else {
        setPwValidationMsg("");
      }

      if (
        currentPass.length === 0 ||
        (hasUpperCase && hasLowerCase && hasSpecialCharacter && hasNumber && lengthValid)
      ) {
        setErrorMessage("");
      }
    }
  }, [
    currentPass,
    newPass,
    resetCode,
    isForceResetPassword,
    isResetPassword,
    setErrorMessage
  ]);

  useEffect(() => {
    if (!errorMessage) {
      setOpenAlert(false);
      setSeverity("");
    }
  }, [errorMessage, setOpenAlert, setSeverity]);

  useEffect(() => {
    if (currentPass && newPass && currentPass === newPass) {
      setPassword(newPass);
      setErrorMessage("");
    }
  }, [currentPass, newPass, setErrorMessage]);

  const updateCode = (e: any) => {
    const code = e.target.value;
    if (code === "") {
      setOpenAlert(false);
      setSeverity("");
    }
    setVerifyCode(code);
  };

  const updateResetCode = (e: any) => {
    const code = e.target.value;
    if (code === "") {
      setOpenAlert(false);
      setSeverity("");
    }
    setResetCode(code);
  };

  const updateUserName = (e: any) => {
    setUserNameValue(e.target.value);
  };

  const updateCurrentPass = (value: string) => {
    setCurrentPass(value);
  };

  const handleSendVerification = async () => {
    if (!userNameValue) {
      setErrorMessage("User name empty!");
      return;
    }

    sendForgotPassCode?.(userNameValue).then((res) => {
      if (res?.wasPassWordResent) {
        cancelWithMessage(
          false,
          "success",
          "Password resent",
          "A new temporary password was resent to the configured email!"
        );
      }
    });
  };

  const handleKeypress = (e: any) => {
    if (authFlowError) {
      setAuthFlowError?.("");
    }
    if (e?.key === "Enter" || e?.keyCode === 13) {
      if (isLegalNotice) {
        handleAcceptTerms?.();
        return;
      }

      if (isForceResetPassword || isResetPassword || isExpiredResetPassword) {
        resetPassword?.(
          finalPassword,
          isResetPassword ? resetCode : null,
          userNameValue,
          oldPass
        );
        return;
      }

      if (isForgotPasswordUsername) {
        handleSendVerification();
      }
    }
  };

  const handleCancel = () => {
    setHasSubmittedMfa(false);
    setViewingPrivacyPolicy(false);
    cancel(false);
    cancelFlow();
    setOpenAlert(false);
    setSeverity("");
  };

  const copyText = async () => {
    const textToCopy = authCode?.text;
    if (!textToCopy) return;

    const copied = copy(textToCopy);
    if (copied) {
      setCopySuccess("Copied!");
    }
  };

  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleMouseDownNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmedPass(!showConfirmedPass);
  const handleMouseDownConfirmPassword = () => setShowConfirmedPass(!showConfirmedPass);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setAuthFlowError?.("");

    if (isMFASetup || isMFAVerify) {

      setHasSubmittedMfa(true);
      handleMFASubmit?.(verifyCode);
      return;
    }

    if (isLegalNotice) {
      handleAcceptTerms?.();
      return;
    }

    if (isForceResetPassword || isResetPassword || isExpiredResetPassword) {
      resetPassword?.(
        finalPassword,
        isResetPassword ? resetCode : null,
        userNameValue,
        oldPass
      );
      return;
    }

    if (isForgotPasswordUsername) {
      handleSendVerification();
    }
  };

  const handlePolicyBack = (e: any) => {
    e.preventDefault();
    setViewingPrivacyPolicy(false);
  };

  const alertStyle = defineAlertStyle(severity);
  const footerButtonsStyle = defineFooterButtonsStyle(isMFASetup);

  const isButtonDisabledProps: DefineIsButtonDisabledProps = {
    currentPass,
    errorMessage,
    expiredResetPass: isExpiredResetPassword,
    forceResetPass: isForceResetPassword,
    getUserName: isForgotPasswordUsername,
    mfaVerify: isMFAVerify,
    newPass,
    oldPass,
    pwValidationMsg,
    setupMFA: isMFASetup,
    userAcceptingTerms: isLegalNotice,
    userNameValue,
    userResetPass: isResetPassword,
    verifyCode,
    resetCode,
    loading,
    onlyNumbersForVerification
  };

  const isButtonDisabled = defineIsButtonDisabled(isButtonDisabledProps);
  return (
    <form style={LoginStyles.formStyle} onSubmit={handleSubmit}>
      <CardHeader
        title={viewConfig.title}
        titleTypographyProps={{
          variant: 'h5',
          color: 'text.primary',
          mb: '0.5rem'
        }}
        subheader={viewConfig.subheader}
        data-testid="card-header"
        subheaderTypographyProps={{
          variant: 'body2',
          color: 'text.secondary'
        }}
      />

      {(isForceResetPassword || isResetPassword) && <ListOfPWRequirements />}

      <CardContent sx={LoginStyles.cardContentStyle}>
        {isMFASetup && (
          <SetUpMFAComponent
            authCode={authCode}
            copyText={copyText}
            copySuccess={copySuccess}
          />
        )}

        {openAlert && (
          <InlineAlert
            severity={severity}
            title={errorMessage}
            message={errorDescription}
            sx={alertStyle}
          />
        )}

        {isLegalNotice && viewingPrivacyPolicy && fullPolicyLanguageComponenet(stateName)}

        {viewConfig.showTextInput && (
          <MFATextInput
            id={viewConfig.inputId}
            label={viewConfig.inputLabel}
            name={viewConfig.inputName}
            onKeyPress={handleKeypress}
            value={viewConfig.inputMode === "username" ? userNameValue : isResetPassword ? resetCode : verifyCode}
            textInput={viewConfig.inputMode === "username" ? updateUserName : isResetPassword ? updateResetCode : updateCode}
            required={true}
            inputRef={inputRef}
            error={isResetPassword && !!resetCode && !!verificationMsg}
            helperText={isResetPassword && resetCode && verificationMsg ? verificationMsg : ""}
            inputProps={
              viewConfig.inputMode === "verification"
                ? { pattern: "[0-9]*", maxLength: 6 }
                : {}
            }
            autoComplete={viewConfig.inputMode === "username" ? "username" : ""}
            type="text"
            variant={mode === 'dark' ? "filled" : "outlined"}
          />
        )}

        {(isForceResetPassword || isResetPassword || isExpiredResetPassword) && (
          <>
            <MFAPwField
              id="new-password"
              label="New Password"
              name="password"
              onChange={(e) => updateCurrentPass(e.target.value)}
              type={showNewPassword ? "text" : "password"}
              inputRef={inputRef}
              autoComplete="new-password"
              variant={mode === 'dark' ? "filled" : "outlined"}
              error={(isForceResetPassword || isExpiredResetPassword) && !!errorMessage}
              helperText={(isForceResetPassword || isExpiredResetPassword) ? errorMessage : ""}
              togglePW={handleClickShowNewPassword}
              toggleMouseDown={handleMouseDownNewPassword}
              showPassword={showNewPassword}
            />
            <MFAPwField
              id="confirm-password"
              label="Confirm Password"
              name="confirm-password"
              onChange={(e) => setNewPass(e.target.value)}
              type={showConfirmedPass ? "text" : "password"}
              inputRef={inputRef}
              autoComplete="new-password"
              variant={mode === 'dark' ? "filled" : "outlined"}
              error={!!pwValidationMsg}
              helperText={pwValidationMsg}
              togglePW={handleClickShowConfirmPassword}
              toggleMouseDown={handleMouseDownConfirmPassword}
              showPassword={showConfirmedPass}
            />
          </>
        )}

        {viewConfig.showFooter && (
          <div style={footerButtonsStyle}>
            {viewConfig.showCancelButton && (
              <MFAPwButton
                onClick={handleCancel}
                variant="outlined"
                color="primary"
                disabled={false}
                style={{ color: 'text.primary', width: isMFASetup ? '50%' : "24%" }}
                label="CANCEL"
              />
            )}

            {!!viewConfig.buttonLabel && (
              <MFAPwButton
                variant="contained"
                type="submit"
                color="secondary"
                btnRef={btnRef}
                loading={loading}
                disabled={isButtonDisabled}
                style={{
                  width: isMFASetup
                    ? '50%'
                    : (isForgotPasswordUsername || isResetPassword || isForceResetPassword || isExpiredResetPassword)
                      ? "100%"
                      : "21%"
                }}
                label={viewConfig.buttonLabel}
              />
            )}
          </div>
        )}

        {(viewConfig.showBackButton || viewingPrivacyPolicy) && (
          <Button
            sx={{ padding: 0, fontSize: '0.8125rem' }}
            color="primary"
            variant="text"
            size="large"
            onClick={viewingPrivacyPolicy ? handlePolicyBack : handleCancel}
            id="cancel"
            data-testid={viewingPrivacyPolicy ? "back-button" : "back-to-signin-button"}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: '0.25rem' }} />
            {viewingPrivacyPolicy ? "BACK" : "BACK TO SIGN IN"}
          </Button>
        )}

        {viewConfig.showLogoFooter && (
          <Grid container justifyContent="center" alignItems="center" direction="column" mt="0.5rem">
            <Typography variant="h3" color="text.primary">{stateLogo}</Typography>
            <img
              src={getAppLogo(isDark)}
              style={{ width: '120px' }}
              alt="The Company logo as the page footer"
            />
          </Grid>
        )}
      </CardContent>
    </form>
  );
};